import type { RouteObject } from 'react-router-dom';
import { lazyWithRetry } from '@repo/error';

import ParkingExistsGuard from '@features/ownedParkingManage/guards/ParkingExistsGuard';
import ParkingManageConsentGuard from '@features/ownedParkingManage/guards/ParkingManageConsentGuard';
import PriceRuleExistsGuard from '@features/ownedParkingManage/guards/PriceRuleExistsGuard';
import SpotExistsGuard from '@features/ownedParkingManage/guards/SpotExistsGuard';
import DashboardDataLayout from '@features/ownedParkingManage/layouts/DashboardDataLayout';
import DashboardLayout from '@features/ownedParkingManage/layouts/DashboardLayout';
import ManageLayout from '@features/ownedParkingManage/layouts/ManageLayout';
import { AuthGuard, PermissionGuard } from '@features/auth';
import ParkingBillingExistsGuard from '@features/ownedParkingManage/guards/ParkingBillingExistsGuard';
import UnavailabilityExistsGuard from '@features/ownedParkingManage/guards/UnavailabilityExistsGuard';
import ParkingVehicleExistsGuard from '@features/ownedParkingManage/guards/ParkingVehicleExistsGuard';
import UnavailabilitiesDataLayout from '@features/ownedParkingManage/layouts/UnavailabilitiesDataLayout';
import PriceRulesDataLayout from '@features/ownedParkingManage/layouts/PriceRulesDataLayout';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

const DashboardPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/DashboardPage'));
const GalleryPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/GalleryPage'));
const ManagePage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/ManagePage'));
const SpotsPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/SpotsPage'));
const MySpotsPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/MySpotsPage'));
const SpotCreatePage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/SpotCreatePage'));
const SpotEditPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/SpotEditPage'));
const DimensionsPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/DimensionsPage'));
const SettingsPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/SettingsPage'));
const LocationPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/LocationPage'));
const PriceRuleCreatePage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/PriceRuleCreatePage'));
const PriceRuleEditPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/PriceRuleEditPage'));
const PriceRulesPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/PriceRulesPage'));
const SignagePage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/SignagePage'));
const GatesPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/GatesPage'));
const BillingSelectionPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/BillingSelectionPage'));
const UnavailabilitiesPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/UnavailabilitiesPage'));
const UnavailabilityCreatePage = lazyWithRetry(
  () => import('@features/ownedParkingManage/pages/UnavailabilityCreatePage')
);
const UnavailabilityEditPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/UnavailabilityEditPage'));
const ParkingVehiclesPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/ParkingVehiclesPage'));
const ParkingVehicleCreatePage = lazyWithRetry(
  () => import('@features/ownedParkingManage/pages/ParkingVehicleCreatePage')
);
const ParkingVehicleEditPage = lazyWithRetry(() => import('@features/ownedParkingManage/pages/ParkingVehicleEditPage'));
const BillingEditPage = lazyWithRetry(() => import('@features/account/pages/BillingEditPage'));

export const ownedParkingManageRoutes: RouteObject[] = [
  {
    element: <ParkingManageConsentGuard />,
    children: [
      {
        element: <AuthGuard />,
        children: [
          {
            element: <DashboardDataLayout />,
            children: [
              {
                element: <DashboardLayout />,
                children: [
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKINGS],
                    element: <DashboardPage />,
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_SPOTS],
                    element: <MySpotsPage />,
                  },
                ],
              },
              {
                element: <ParkingExistsGuard />,
                children: [
                  {
                    element: <ManageLayout />,
                    children: [
                      {
                        path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE],
                        element: <ManagePage />,
                      },
                    ],
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_SETTINGS],
                    element: <SettingsPage />,
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_LOCATION],
                    element: <LocationPage />,
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_BILLING_SELECTION],
                    element: <BillingSelectionPage />,
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_BILLING_EDIT],
                    element: <ParkingBillingExistsGuard />,
                    children: [
                      {
                        index: true,
                        element: <BillingEditPage source="parkingManage" />,
                      },
                    ],
                  },
                  {
                    element: <PermissionGuard permissions={['parking.vehicle.view']} />,
                    children: [
                      {
                        path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_VEHICLES],
                        element: <ParkingVehiclesPage />,
                      },
                    ],
                  },
                  {
                    element: <PermissionGuard permissions={['parking.vehicle.update']} />,
                    children: [
                      {
                        path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_VEHICLES_CREATE],
                        element: <ParkingVehicleCreatePage />,
                      },
                      {
                        path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_VEHICLES_EDIT],
                        element: <ParkingVehicleExistsGuard />,
                        children: [
                          {
                            index: true,
                            element: <ParkingVehicleEditPage />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_DIMENSIONS],
                    element: <DimensionsPage />,
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_GALLERY],
                    element: <GalleryPage />,
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_SPOTS],
                    element: <SpotsPage />,
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_SPOTS_CREATE],
                    element: <SpotCreatePage />,
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_SPOTS_EDIT],
                    element: <SpotExistsGuard />,
                    children: [
                      {
                        index: true,
                        element: <SpotEditPage />,
                      },
                    ],
                  },
                  {
                    element: <PriceRulesDataLayout />,
                    children: [
                      {
                        path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_PRICE_RULES],
                        element: <PriceRulesPage />,
                      },
                      {
                        path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_PRICE_RULES_EDIT],
                        element: <PriceRuleExistsGuard />,
                        children: [
                          {
                            index: true,
                            element: <PriceRuleEditPage />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_PRICE_RULES_CREATE],
                    element: <PriceRuleCreatePage />,
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_SIGNAGE],
                    element: <SignagePage />,
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_GATES],
                    element: <GatesPage />,
                  },
                  {
                    element: <UnavailabilitiesDataLayout />,
                    children: [
                      {
                        path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_UNAVAILABILITIES],
                        element: <UnavailabilitiesPage />,
                      },
                      {
                        path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_UNAVAILABILITY_EDIT],
                        element: <UnavailabilityExistsGuard />,
                        children: [
                          {
                            index: true,
                            element: <UnavailabilityEditPage />,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: ROUTE_PATHS[RouteName.MY_PARKING_MANAGE_UNAVAILABILITY_CREATE],
                    element: <UnavailabilityCreatePage />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];
